const root = window.Shopify.routes.root;

export const cart = {
  data: {},
  // Open state
  open: false,
  // Store cart items
  items: [],
  // Store cart loading state
  loading: false,
  // Store cart error
  error: null,
  // Store cart total price
  totalPrice: null,
  // Store cart quantity
  quantity: null,
  // Shipping Protection
  shippingProtection: false,
  shippingProtectionId: null,

  // Formatted total price
  get formattedTotalPrice() {
    if (this.totalPrice === null) return null;
    return this.formatMoney(this.totalPrice);
  },

  get totalItems() {
    return this.items?.length || 0;
  },

  // Methods
  // Toggle cart
  toggle() {
    this.open = !this.open;
  },
  close() {
    this.open = false;
  },

  // Add variant to cart
  async addVariant(variantId, quantity = 1) {
    this.loading = `${variantId}`;
    await this.sendRequest(
      "/cart/add.js",
      "POST",
      { id: variantId, quantity: quantity },
      "cart:add"
    );
  },
  // Update variant quantity
  async updateQuantity(lineItemId, quantity) {
    this.loading = `${lineItemId}`;
    const updates = {};
    updates[lineItemId] = quantity;
    await this.sendRequest(
      "/cart/update.js",
      "POST",
      { updates },
      "cart:update"
    );
  },
  // Remove variant from cart
  async removeVariant(lineItemId) {
    this.loading = `${lineItemId}`;
    const updates = {};
    updates[lineItemId] = 0;
    await this.sendRequest(
      "/cart/update.js",
      "POST",
      { updates },
      "cart:remove"
    );
  },
  // Update cart note
  async updateNote(note) {
    await this.sendRequest("/cart/update.js", "POST", { note }, "cart:update");
  },
  // Clear cart
  async clear() {
    await this.sendRequest("/cart/clear.js", "POST", {}, "cart:clear");
  },
  // Apply discount code
  async applyDiscountCode(code) {
    this.loading = "discount";
    try {
      await fetch(`/discount/${code}`);
    } catch (error) {
      console.log(error);
    }
    await this.refresh();
  },
  // Remove discount code
  async removeDiscountCode() {
    this.sendRequest("/cart/discount.json", "DELETE", {}, "cart:discount");
  },

  // Helpers
  // Emit events
  emit(eventName, payload = null) {
    const event = new CustomEvent(eventName, { detail: payload });
    window.dispatchEvent(event);
  },
  // Refresh cart items
  async refresh() {
    try {
      const data = await fetchCartFromShopify();
      this.items = data.items;
      this.totalPrice = data.total_price;
      this.quantity = data.item_count;
      this.loading = false;
      this.emit("cart:refresh");
      this.data = data;
      // console.log(this);
    } catch (error) {
      this.error = error;
      this.loading = false;
    }
  },

  async sendRequest(route, method, body, event) {
    // if route starts with '/' remove the slash and prepend the root
    if (route.startsWith("/")) {
      route = root + route.slice(1);
    } else {
      route = root + route;
    }

    try {
      await fetch(route, {
        method: method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const data = await this.refresh();
      this.loading = false;
      this.emit(event, data);
    } catch (error) {
      this.error = error;
      this.loading = false;
      this.emit("cart:error", error);
    }
  },
  // Format money
  formatMoney(cents, format) {
    if (cents === 0) return formatMoney(0, format);
    if (!cents) return "";
    return formatMoney(cents, format);
  },
  // Adjust scroll on html el
  scrollLock() {
    const html = document.documentElement;
    if (this.open) html.style.overflow = "hidden";
    if (!this.open) html.style.overflow = null;
  },

  // Shipping Protection
  async toggleShippingProtection(event) {
    // Define state
    const checked = event.target.checked;
    const shippingProtectionId = Number(this.shippingProtectionId);
    if (!shippingProtectionId) return;
    // Check if cart contains Shipping Protection
    const cartContainsShippingProtection = this.items?.some(
      (item) => item.id === shippingProtectionId
    );
    this.loading = "protection";

    // When user toggles shipping protection, add or remove from cart
    if (checked && !cartContainsShippingProtection) {
      await this.addVariant(shippingProtectionId);
    }

    // Remove all shipping protection items
    if (!checked && cartContainsShippingProtection) {
      const allShippingItems = this.items?.filter(
        (i) => i.id === shippingProtectionId
      );
      // Create updates object
      const updates = {};
      allShippingItems.forEach((i) => {
        updates[i.key] = 0;
      });

      await this.sendRequest(
        "/cart/update.js",
        "POST",
        { updates },
        "cart:remove"
      );
    }
    // Remove loading state
    this.loading = false;
  },

  // Init
  async init() {
    await this.refresh();

    // Get Shipping Protection item, if it exists
    const shippingProtectionInput = document.querySelector(
      "#cart-shipping-protection"
    );

    const shippingProtectionId =
      shippingProtectionInput?.dataset.shippingProductId;

    // Set initial state of shipping ProtectionBox
    if (shippingProtectionId) {
      this.shippingProtectionId = Number(shippingProtectionId);
      const cartContainsShippingProtection = this.items?.some(
        (item) => item.id === this.shippingProtectionId
      );
      shippingProtectionInput.checked = cartContainsShippingProtection;
    }

    // Watch and respond to change of cart items
    Alpine.effect(() => {
      if (!shippingProtectionId) return;
      const cartContainsShippingProtection = this.items?.some(
        (item) => item.id === this.shippingProtectionId
      );

      if (cartContainsShippingProtection) {
        shippingProtectionInput.checked = true;
      } else {
        shippingProtectionInput.checked = false;
      }
    });

    // ScrollLock
    Alpine.effect(() => {
      this.scrollLock();
    });

    // Check if the cart is open in the URL
    const url = new URL(location);
    const open = url.searchParams.get("cart");
    if (open) {
      this.open = true;
    }

    // Open the cart on any change
    window.addEventListener("cart:add", () => {
      this.open = true;
    });
  },
};

// Helper functions
async function fetchCartFromShopify() {
  const response = await fetch("/cart.js", { method: "GET" });
  const data = await response.json();
  return data;
}

function formatMoney(cents, format) {
  if (cents === 0) {
    return "$0";
  }

  if (typeof cents === "string") {
    cents = cents?.replace(".", "");
  }
  let value = "";
  // const placeholderRegex = /\{\s*(\w+)\s*\}/;
  const formatString = format || "amount";

  function formatWithDelimiters(number, precision = 2) {
    const thousands = ",";
    const decimal = ".";
    if (isNaN(number) || number == null) {
      return 0;
    }
    number = (number / 100.0).toFixed(precision);
    const parts = number.split(".");
    const dollarsAmount = parts[0]?.replace(
      /(\d)(?=(\d\d\d)+(?!\d))/g,
      `$1${thousands}`
    );
    const centsAmount = parts[1] ? decimal + parts[1] : "";
    return dollarsAmount + centsAmount;
  }

  switch (formatString) {
    case "amount":
      value = formatWithDelimiters(cents, 2)?.replace(/[.,]00$/, "");
      break;
    case "amount_no_decimals":
      value = formatWithDelimiters(cents, 0);
      break;
    case "amount_with_comma_separator":
      value = formatWithDelimiters(cents, 2)?.replace(/\./, ",");
      break;
    case "amount_no_decimals_with_comma_separator":
      value = formatWithDelimiters(cents, 0)?.replace(/\./, ",");
      break;
  }
  return "$" + value;
}
